// frontend/src/components/UserProfileEditor.js
//#region Imports
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { useAlertContext } from '../../contexts/AlertContext';
import estado from '../../utils/estados.json';
import { API_ENDPOINTS, EXTERNAL_API } from '../../configs/apiConfig';
import translations from './translations';
import BrandSelect from '../common/BrandSelect';
import { FaArrowLeft, FaCheck, FaKey } from 'react-icons/fa';
//#endregion

//#region Componente Principal
function UserProfileEditor() {
  //#region Declarações
  const { username: contextUsername } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [cropData, setCropData] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showOccupationModal, setShowOccupationModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [pendingOccupation, setPendingOccupation] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const [userData, setUserData] = useState({
    username: '',
    name: '',
    email: '',
    role: '',
    subtype: '',
    cpf: '',
    dateBirth: '',
    state: '',
    city: '',
    company: '',
    educationalInstitution: '',
    areaplanted: '',
    plantingChallengesDescription: '',
    cropType: '',
    isInvolvedWithBiological: null,
    isFarmerChild: null,
    conductsCoffeeResearch: null,
    permissionToCollectGeographicData: null,
    platformInterest: '',
    otherDescription: '',
    replacePassword: '',
  });

  const { showSuccess, showError } = useAlertContext();

  const t = translations[language] || translations.pt;
  //#endregion

  //#region Efeitos (useEffect)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const auth = localStorage.getItem('auth');
        const response = await axios.get(API_ENDPOINTS.USERS_BY_USERNAME(contextUsername), {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        const normalizedData = {};
        Object.keys(response.data).forEach(key => {
          if (typeof response.data[key] === 'boolean') {
            normalizedData[key] = response.data[key] || false;
          } else {
            normalizedData[key] = response.data[key] || '';
          }
        });

        setUserData(normalizedData);
      } catch (error) {
        showError(`${t.errorLoading} ${error.message}`);
      }
    };

    const fetchCropData = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.PLANTING_TYPES);
        setCropData(response.data);
      } catch (error) {
        console.error('Error fetching crop types:', error);
      }
    };

    if (contextUsername) {
      fetchUserData();
      fetchCropData();
    }
  }, [contextUsername, t.errorLoading, showError]);

  useEffect(() => {
    const fetchCities = async () => {
      if (userData.state) {
        try {
          const response = await axios.get(
            EXTERNAL_API.IBGE_CITIES_BY_STATE(userData.state)
          );
          setCityList(response.data);
        } catch (error) {
          console.error('Error fetching cities:', error);
        }
      }
    };
    fetchCities();
  }, [userData.state]);
  //#endregion

  //#region Funções de Validação
  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
      messages: [
        password.length >= minLength ? '' : t.passwordErrorMinLength,
        hasUpperCase ? '' : t.passwordErrorUpperCase,
        hasLowerCase ? '' : t.passwordErrorLowerCase,
        hasNumber ? '' : t.passwordErrorNumber,
        hasSpecialChar ? '' : t.passwordErrorSpecialChar
      ].filter(msg => msg !== '')
    };
  };

  const validateOccupationFields = () => {
    const requiredFields = {
      PRODUCER: ['cropType', 'areaplanted', 'plantingChallengesDescription'],
      STUDENT: ['educationalInstitution'],
      CONSULTANT: ['company', 'isInvolvedWithBiological'],
      RESEARCHER: ['educationalInstitution', 'conductsCoffeeResearch', 'isInvolvedWithBiological'],
      OTHER: ['otherDescription']
    };

    const fieldsToValidate = requiredFields[userData.subtype] || [];
    const missingFields = [];

    fieldsToValidate.forEach(field => {
      if (field === 'isInvolvedWithBiological' || field === 'conductsCoffeeResearch') {
        if (userData[field] === null || userData[field] === undefined) {
          missingFields.push(field);
        }
      } else {
        if (userData[field] === null || (typeof userData[field] === 'string' && userData[field].trim() === '')) {
          missingFields.push(field);
        }
      }
    });

    return missingFields;
  };

  const verifyOccupationEmptyness = () => {
    if (!userData.company && !userData.educationalInstitution && !userData.areaplanted && !userData.plantingChallengesDescription && !userData.cropType &&
      userData.isInvolvedWithBiological === null && userData.isFarmerChild === null &&
      userData.conductsCoffeeResearch === null && !userData.otherDescription) {
      return true;
    }
    return false;
  };
  //#endregion

  //#region Manipulação de Dados
  const handleOccupationChange = (newOccupation) => {
    if (userData.subtype && userData.subtype !== newOccupation && !verifyOccupationEmptyness()) {
      setPendingOccupation(newOccupation);
      setShowOccupationModal(true);
    } else {
      setUserData(prev => ({ ...prev, subtype: newOccupation }));
    }
  };

  const confirmOccupationChange = () => {
    setUserData(prev => ({
      ...prev,
      subtype: pendingOccupation,
      company: '',
      educationalInstitution: '',
      areaplanted: '',
      plantingChallengesDescription: '',
      cropType: '',
      isInvolvedWithBiological: null,
      isFarmerChild: null,
      conductsCoffeeResearch: null,
      otherDescription: ''
    }));
    setShowOccupationModal(false);
    setPendingOccupation('');
  };

  const handlePasswordChange = async () => {
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setPasswordError(validation.messages.join(', '));
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setConfirmPasswordError(t.passwordMismatch || 'Senhas não coincidem');
      return;
    }

    const requiredBasicFields = ['name', 'dateBirth', 'state', 'city', 'subtype'];
    const missingBasicFields = requiredBasicFields.filter(field =>
      !userData[field] || (typeof userData[field] === 'string' && userData[field].trim() === '')
    );

    if (missingBasicFields.length > 0) {
      const fieldLabels = {
        username: t.username,
        name: t.fullName,
        email: t.email,
        cpf: t.cpf,
        dateBirth: t.dateOfBirth,
        state: t.state,
        city: t.city,
        subtype: t.occupation
      };
      const errorMessage = `${t.missingFields} ${missingBasicFields.map(f => fieldLabels[f]).join(', ')}`;
      showError(errorMessage);
      setShowPasswordModal(false);
      return;
    }

    const missingFields = validateOccupationFields();
    if (missingFields.length > 0) {
      const errorMessage = `${t.missingFields} ${missingFields.map(f => t.fieldNames[f] || f).join(', ')}`;
      showError(errorMessage);
      setShowPasswordModal(false);
      return;
    }

    try {
      const auth = localStorage.getItem('auth');
      await axios.put(
        API_ENDPOINTS.USERS_BY_USERNAME(contextUsername),
        { ...userData, replacePassword: newPassword },
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );

      localStorage.removeItem('auth');
      showSuccess(t.passwordChangedLogout || 'Senha alterada com sucesso! Você será redirecionado para o login.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);

    } catch (error) {
      showError(`${t.errorMessage} ${error.message}`);
    }

    setShowPasswordModal(false);
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordError('');
    setConfirmPasswordError('');
  };

  const handleChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const auth = localStorage.getItem('auth');
      await axios.put(
        API_ENDPOINTS.USERS_BY_USERNAME(contextUsername),
        userData,
        {
          headers: {
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );
      showSuccess(t.successMessage);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1800);
    } catch (error) {
      showError(`${t.errorMessage} ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getSafeValue = (value) => {
    return value !== null && value !== undefined ? value : '';
  };

  // Controle segmentado Não/Sim (no lugar dos radios genéricos)
  const renderBool = (field, label, helper) => (
    <div className="profile-field">
      <label className="form-label">{label}</label>
      <div className="seg-toggle" role="group" aria-label={label}>
        <button
          type="button"
          className={`seg-btn${userData[field] === false ? ' active' : ''}`}
          onClick={() => handleChange(field, false)}
        >
          {t.no}
        </button>
        <button
          type="button"
          className={`seg-btn${userData[field] === true ? ' active' : ''}`}
          onClick={() => handleChange(field, true)}
        >
          {t.yes}
        </button>
      </div>
      {helper && <div className="field-help">{helper}</div>}
    </div>
  );
  //#endregion

  //#region Dados derivados
  const initials = (userData.name || userData.username || '?')
    .trim().split(/\s+/).slice(0, 2).map(s => (s[0] || '').toUpperCase()).join('') || '?';
  const roleClass = userData.role ? `role-${String(userData.role).toLowerCase()}` : '';
  //#endregion

  //#region Renderização
  return (
    <div className="profile-editor container" style={{ maxWidth: '760px' }}>
      {/* Cabeçalho-resumo */}
      <button type="button" className="btn btn-link p-0 mb-2 text-decoration-none profile-back" onClick={() => navigate(-1)}>
        <FaArrowLeft className="me-2" />{t.back}
      </button>

      <div className="profile-head">
        <div className="profile-avatar">{initials}</div>
        <div className="flex-grow-1">
          <h2 className="profile-name">{userData.name || userData.username || '—'}</h2>
          <div className="profile-meta">
            {userData.email && <span>{userData.email}</span>}
            {userData.role && <span className={`role-badge ${roleClass}`}>{userData.role}</span>}
          </div>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowPasswordModal(true)}>
          <FaKey className="me-2" />{t.changePassword}
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ① IDENTIDADE */}
        <section className="profile-section">
          <h3 className="profile-section-title">{t.secIdentity}</h3>
          <div className="profile-field">
            <label className="form-label">{t.fullName}</label>
            <input
              type="text"
              className="form-control form-control-lg"
              value={getSafeValue(userData.name)}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </div>
          <div className="row g-3">
            <div className="col-sm-6 profile-field mb-0">
              <label className="form-label">{t.cpf}</label>
              <input
                type="text"
                className="form-control"
                value={userData.cpf}
                onChange={(e) => handleChange('cpf', formatCPF(e.target.value))}
                maxLength="14"
                placeholder="XXX.XXX.XXX-XX"
              />
            </div>
            <div className="col-sm-6 profile-field mb-0">
              <label className="form-label">{t.dateOfBirth}</label>
              <input
                type="date"
                className="form-control"
                value={getSafeValue(userData.dateBirth)}
                onChange={(e) => handleChange('dateBirth', e.target.value)}
                required
              />
            </div>
          </div>
        </section>

        {/* ② LOCALIZAÇÃO */}
        <section className="profile-section">
          <h3 className="profile-section-title">{t.location}</h3>
          <div className="row g-3">
            <div className="col-sm-6 profile-field mb-0">
              <label className="form-label">{t.state}</label>
              <BrandSelect
                className="w-100"
                searchable
                searchPlaceholder={t.searchPlaceholder}
                noResultsText={t.noResults}
                value={getSafeValue(userData.state)}
                onChange={(v) => handleChange('state', v)}
                placeholder={t.selectState}
                options={[
                  { value: "", label: t.selectState },
                  ...estado.map((est) => ({ value: est.sigla, label: est.nome })),
                ]}
              />
            </div>
            <div className="col-sm-6 profile-field mb-0">
              <label className="form-label">{t.city}</label>
              <BrandSelect
                className="w-100"
                searchable
                searchPlaceholder={t.searchPlaceholder}
                noResultsText={t.noResults}
                value={getSafeValue(userData.city)}
                onChange={(v) => handleChange('city', v)}
                placeholder={t.selectCity}
                options={[
                  { value: "", label: t.selectCity },
                  ...cityList.map((city) => ({ value: city.nome, label: city.nome })),
                ]}
              />
            </div>
          </div>
        </section>

        {/* ③ ATUAÇÃO PROFISSIONAL */}
        <section className="profile-section">
          <h3 className="profile-section-title">{t.secOccupation}</h3>
          <div className="profile-field">
            <label className="form-label">{t.howDoYouWork}</label>
            <BrandSelect
              className="w-100"
              value={userData.subtype}
              onChange={(v) => handleOccupationChange(v)}
              placeholder={t.selectOccupation}
              options={[
                { value: "", label: t.selectOccupation },
                { value: "STUDENT", label: t.student },
                { value: "CONSULTANT", label: t.consultant },
                { value: "RESEARCHER", label: t.researcher },
                { value: "PRODUCER", label: t.producer },
                { value: "OTHER", label: t.other },
              ]}
            />
            <div className="field-help">{t.helperOccupation}</div>
          </div>

          {userData.subtype === 'PRODUCER' && (
            <div className="profile-subblock">
              <div className="profile-field">
                <label className="form-label">{t.cropType}</label>
                <BrandSelect
                  className="w-100"
                  searchable
                  searchPlaceholder={t.searchPlaceholder}
                  noResultsText={t.noResults}
                  value={userData.cropType}
                  onChange={(v) => handleChange('cropType', v)}
                  placeholder={t.selectCropType}
                  options={[
                    { value: "", label: t.selectCropType },
                    ...cropData.map((crop) => ({ value: crop, label: crop })),
                  ]}
                />
              </div>
              {userData.cropType && (
                <div className="profile-field">
                  <label className="form-label">{t.plantedArea} {userData.cropType} (ha)*</label>
                  <input
                    type="number"
                    className="form-control"
                    value={getSafeValue(userData.areaplanted)}
                    onChange={(e) => handleChange('areaplanted', e.target.value)}
                    placeholder={t.placeholderArea}
                    required
                  />
                </div>
              )}
              <div className="profile-field mb-0">
                <label className="form-label">{t.plantingChallenges}</label>
                <textarea
                  className="form-control"
                  value={getSafeValue(userData.plantingChallengesDescription)}
                  onChange={(e) => handleChange('plantingChallengesDescription', e.target.value)}
                  rows="3"
                  placeholder={t.plantingChallengesPlaceholder}
                  required
                />
              </div>
            </div>
          )}

          {userData.subtype === 'STUDENT' && (
            <div className="profile-subblock">
              <div className="profile-field">
                <label className="form-label">{t.educationalInstitution}</label>
                <input
                  type="text"
                  className="form-control"
                  value={getSafeValue(userData.educationalInstitution)}
                  onChange={(e) => handleChange('educationalInstitution', e.target.value)}
                  required
                />
              </div>
              {renderBool('isFarmerChild', t.isFarmerChild)}
              {userData.isFarmerChild && (
                <>
                  <div className="profile-field">
                    <label className="form-label">{t.cropType}</label>
                    <BrandSelect
                      className="w-100"
                      searchable
                      searchPlaceholder={t.searchPlaceholder}
                      noResultsText={t.noResults}
                      value={getSafeValue(userData.cropType)}
                      onChange={(v) => handleChange('cropType', v)}
                      placeholder={t.selectCropType}
                      options={[
                        { value: "", label: t.selectCropType },
                        ...cropData.map((crop) => ({ value: crop, label: crop })),
                      ]}
                    />
                  </div>
                  {userData.cropType && (
                    <div className="profile-field mb-0">
                      <label className="form-label">{t.plantedArea} {userData.cropType} (ha)*</label>
                      <input
                        type="number"
                        className="form-control"
                        value={getSafeValue(userData.areaplanted)}
                        onChange={(e) => handleChange('areaplanted', e.target.value)}
                        placeholder={t.placeholderArea}
                        required
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {userData.subtype === 'CONSULTANT' && (
            <div className="profile-subblock">
              <div className="profile-field">
                <label className="form-label">{t.company}</label>
                <input
                  type="text"
                  className="form-control"
                  value={getSafeValue(userData.company)}
                  onChange={(e) => handleChange('company', e.target.value)}
                  required
                />
              </div>
              {renderBool('isInvolvedWithBiological', t.involvedWithBiological, t.helperBiological)}
            </div>
          )}

          {userData.subtype === 'RESEARCHER' && (
            <div className="profile-subblock">
              <div className="profile-field">
                <label className="form-label">{t.educationalInstitution}</label>
                <input
                  type="text"
                  className="form-control"
                  value={getSafeValue(userData.educationalInstitution)}
                  onChange={(e) => handleChange('educationalInstitution', e.target.value)}
                  required
                />
              </div>
              {renderBool('conductsCoffeeResearch', t.conductsCoffeeResearch, t.helperResearch)}
              {renderBool('isInvolvedWithBiological', t.usesBiologicalInResearch, t.helperBiological)}
            </div>
          )}

          {userData.subtype === 'OTHER' && (
            <div className="profile-subblock">
              <div className="profile-field mb-0">
                <label className="form-label">{t.otherDescription}</label>
                <input
                  type="text"
                  className="form-control"
                  value={getSafeValue(userData.otherDescription)}
                  onChange={(e) => handleChange('otherDescription', e.target.value)}
                  placeholder={t.placeholderOtherDescription}
                  required
                />
              </div>
            </div>
          )}
        </section>

        {/* ④ PREFERÊNCIAS */}
        <section className="profile-section">
          <h3 className="profile-section-title">{t.secPreferences}</h3>
          <div className="profile-field mb-0">
            <label className="form-label">{t.platformInterestQuestion}</label>
            <textarea
              className="form-control"
              value={getSafeValue(userData.platformInterest)}
              onChange={(e) => handleChange('platformInterest', e.target.value)}
              rows="3"
              placeholder={t.platformInterestPlaceholder}
            />
            <div className="field-help">{t.helperInterest}</div>
          </div>
        </section>

        {/* Footer fixo */}
        <div className="profile-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
            {t.cancel}
          </button>
          <button type="submit" className={`btn ${justSaved ? 'btn-success' : 'btn-brand'}`} disabled={saving}>
            {saving ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{t.saving}</>
            ) : justSaved ? (
              <><FaCheck className="me-2" />{t.saved}</>
            ) : (
              t.saveChanges
            )}
          </button>
        </div>
      </form>

      {showPasswordModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t.changePassword}</h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label>{t.newPassword}</label>
                  <input
                    type="password"
                    className={`form-control ${passwordError && 'is-invalid'}`}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordError('');
                    }}
                    onBlur={(e) => {
                      const validation = validatePassword(e.target.value);
                      if (!validation.isValid) {
                        setPasswordError(validation.messages.join(', '));
                      }
                    }}
                  />
                  {passwordError && <div className="invalid-feedback">{passwordError}</div>}
                </div>
                <div className="mb-3">
                  <label>{t.confirmNewPassword}</label>
                  <input
                    type="password"
                    className={`form-control ${confirmPasswordError && 'is-invalid'}`}
                    value={confirmNewPassword}
                    onChange={(e) => {
                      setConfirmNewPassword(e.target.value);
                      setConfirmPasswordError('');
                    }}
                    onBlur={(e) => {
                      if (e.target.value !== newPassword) {
                        setConfirmPasswordError(t.passwordMismatch);
                      }
                    }}
                  />
                  {confirmPasswordError && <div className="invalid-feedback">{confirmPasswordError}</div>}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmNewPassword('');
                    setPasswordError('');
                    setConfirmPasswordError('');
                  }}
                >
                  {t.cancel}
                </button>
                <button
                  type="button"
                  className="btn btn-brand"
                  onClick={handlePasswordChange}
                >
                  {t.confirmChange}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showOccupationModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t.occupation}</h5>
              </div>
              <div className="modal-body">
                <p>{t.occupationChangeWarning}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowOccupationModal(false);
                    setPendingOccupation('');
                  }}
                >
                  {t.cancel}
                </button>
                <button
                  type="button"
                  className="btn btn-brand"
                  onClick={confirmOccupationChange}
                >
                  {t.confirmChange}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  //#endregion
}

export default UserProfileEditor;
